import React, { useState } from "react";
import { FaCheck, FaGlobe, FaSearch } from "react-icons/fa";
import BottomSheet from "./BottomSheet";
import LanguageFlag from "./LanguageFlag";
import { LANGUAGES, Lang } from "../i18n/languages";
import { offeredLanguages, currentLanguage, savedLanguageChoice, saveLanguageChoice, phoneLanguage, formatLocale, t } from "../i18n";
import "./LanguageSheet.css";

// The language picker (the talent's Profile drawer, just above its footer
// line since 1.5.2 b45; the fan ⋯ menu). Out of the box the app
// follows the phone. When the phone speaks one of our languages, that language
// IS the automatic choice: it sits on top, already ticked, and no "Automatic"
// row is shown (founder, 2026-10-19: "will make us look more premium"). Only a
// phone in a language we don't offer gets "Automatic (phone language)" on top,
// which means English. Picking a language saves it on this device and
// reloads, the way Spotify restarts; picking the phone's own language again
// goes back to following the phone.
// Hidden until a second language is offered (src/i18n offeredLanguages:
// READY on the website and, from 1.5.2, in the phone app).

export const languagePickerAvailable = (): boolean => offeredLanguages().length > 1;

/** The name to show beside a "Language" row: the current language, in itself. */
export const currentLanguageName = () => LANGUAGES.find((l) => l.code === currentLanguage())?.name ?? "English";

type Row = { key: string; lang: Lang | null; name: string; choice: Lang | null; search: string };

// Every name a viewer might type: the language in itself, in English and in the app's language.
function searchText(code: Lang, name: string): string {
  const names = [name, code];
  for (const locale of ["en", formatLocale()]) {
    try {
      if (locale) names.push(new Intl.DisplayNames([locale], { type: "language" }).of(code) ?? "");
    } catch {
      /* old browser without Intl.DisplayNames: the native name still matches */
    }
  }
  return names.join(" ").toLowerCase();
}

export default function LanguageSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [query, setQuery] = useState("");
  const phone = phoneLanguage();
  const saved = savedLanguageChoice() === phone ? null : savedLanguageChoice();

  const choose = (choice: Lang | null) => {
    if (choice === saved) return onClose();
    saveLanguageChoice(choice);
    window.location.reload();
  };

  const automatic = t("languageSheet.automatic");
  const rows: Row[] = [
    ...(phone ? [] : [{ key: "auto", lang: null, name: automatic, choice: null, search: automatic.toLowerCase() }]),
    ...LANGUAGES.filter((l) => offeredLanguages().includes(l.code)).map((l) => ({
      key: l.code,
      lang: l.code,
      name: l.name,
      choice: l.code === phone ? null : l.code,
      search: searchText(l.code, l.name),
    })),
  ];
  const q = query.trim().toLowerCase();
  const shown = rows.filter((r) => !q || r.search.includes(q));
  const picked = shown.find((r) => r.choice === saved);
  const others = shown.filter((r) => r !== picked);

  const row = (r: Row) => {
    const on = r === picked;
    return (
      <button
        key={r.key}
        type="button"
        role="radio"
        aria-checked={on}
        className={`lang-row${on ? " lang-row--on" : ""}`}
        onClick={() => choose(r.choice)}
      >
        {r.lang ? <LanguageFlag lang={r.lang} /> : <span className="lang-flag lang-flag--auto" aria-hidden="true"><FaGlobe /></span>}
        <span className="lang-row-name" lang={r.lang ?? undefined}>{r.name}</span>
        {on ? <FaCheck className="lang-row-check" aria-hidden /> : <span className="lang-row-radio" aria-hidden="true" />}
      </button>
    );
  };

  return (
    <BottomSheet open={open} title={t("languageSheet.title")} onClose={onClose} className="lang-bsheet" roomy>
      <p className="lang-sub">{t("languageSheet.subtitle")}</p>
      <label className="lang-search">
        <FaSearch className="lang-search-ic" aria-hidden />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("languageSheet.search")}
          aria-label={t("languageSheet.search")}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
        />
      </label>
      <div className="lang-sheet" role="radiogroup" aria-label={t("languageSheet.title")}>
        {picked && <div className="lang-picked">{row(picked)}</div>}
        {others.length > 0 && <div className="lang-list">{others.map(row)}</div>}
        {shown.length === 0 && <p className="lang-empty">{t("languageSheet.noResults")}</p>}
      </div>
    </BottomSheet>
  );
}
